/**
 * LanguagePatterns class to encapsulate language pattern data
 * This approach prevents minification from mangling language property names
 */
export class LanguagePatterns {
  private static _patterns = new Map<string, RegExp[]>();

  /**
   * Initialize all language patterns
   * Called once at startup
   */
  static initialize(): void {
    this._patterns.set('afrikaans', [
      /\bdie\b|\ben\b|\bvan\b|\bin\b|\bte\b|\bhet\b|\bis\b|\bdat\b|\bnie\b|\bvir\b|\bmet\b|\bom\b/gi, /[êëîïôûáéíóúý]/gi]);

    this._patterns.set('albanian', [
      /\bdhe\b|\bqë\b|\bnë\b|\btë\b|\bpër\b|\bnga\b|\bme\b|\be\b|\bpa\b|\bnuk\b|\bmbi\b/gi, /[çë]/gi]);

    this._patterns.set('amharic', [
      /[\u1200-\u137F]/g, /የ|ነው|ላይ|ወይም|ከ|እንደ|እና/g]);

    this._patterns.set('arabic', [
      /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/g, /\bال\b|\bفي\b|\bمن\b|\bعلى\b|\bإلى\b|\bعن\b|\bمع\b|\bهذا\b|\bهذه\b|\bذلك\b/gi]);

    this._patterns.set('armenian', [
      /[\u0530-\u058F]/g, /և|է|որ|ին|ը|մի|ով|այն|ենք/g]);

    this._patterns.set('assamese', [
      /[\u0980-\u09FF]/g, /এই|আৰু|হয়|হৈছে|কৰা|কৰিব|লগতে/g]);

    this._patterns.set('azerbaijani_cyrillic', [
      /[\u04D8\u04D9\u0492\u0493\u049A\u049B\u049C\u049D\u04A2\u04A3\u04AE\u04AF\u04B0\u04B1\u04B8\u04B9\u04BA\u04BB]/g, /\bвә\b|\bбу\b|\bвар\b|\bки\b|\bда\b|\bмән\b/gi]);

    this._patterns.set('azerbaijani_latin', [
      /\bvə\b|\bbu\b|\bvar\b|\bki\b|\bda\b|\bmən\b|\bola\b|\bilə\b|\bolan\b|\bonun\b/gi, /[əçğşü]/gi]);

    this._patterns.set('belarusian', [
      /[\u0400-\u04FF]/g, /\bі\b|\bў\b|\bне\b|\bна\b|\bз\b|\bгэта\b|\bяк\b|\bто\b/gi, /[ёіўґ]/gi]);

    this._patterns.set('bengali', [
      /[\u0980-\u09FF]/g, /এই|আর|হয়|হয়েছে|করা|করবে|সঙ্গে/g]);

    this._patterns.set('bosnian', [
      /\bi\b|\bu\b|\bje\b|\bse\b|\bda\b|\bna\b|\bsu\b|\bne\b|\bs\b|\bkao\b|\bza\b|\bto\b|\bšto\b/gi, /[čćđšž]/gi]);

    this._patterns.set('bulgarian', [
      /[\u0410-\u044F]/g, /\bи\b|\bв\b|\bе\b|\bна\b|\bсе\b|\bда\b|\bс\b|\bне\b|\bот\b|\bкато\b|\bза\b|\bтова\b/gi]);

    this._patterns.set('burmese', [
      /[\u1000-\u109F]/g, /မှာ|ကို|သည်|တွင်|နှင့်|ဖြစ်|သော|ရန်|သို့|လည်း|များ|ရှိ|အား|ပြီး|ဟု|သောအခါ/g]);

    this._patterns.set('catalan', [
      /\bel\b|\bla\b|\bde\b|\bi\b|\bque\b|\ben\b|\bun\b|\buna\b|\bper\b|\bamb\b|\bes\b|\bno\b/gi, /[àçèéíïòóúü]/gi]);

    this._patterns.set('chinese', [
      /[\u4E00-\u9FFF\u3400-\u4DBF]/g, /的|了|和|是|在|有|我|他|她|你|们|这|那/g]);

    this._patterns.set('croatian', [
      /\bi\b|\bu\b|\bje\b|\bse\b|\bda\b|\bna\b|\bsu\b|\bne\b|\bs\b|\bkao\b|\bza\b|\bto\b|\bšto\b|\bali\b/gi, /[čćđšž]/gi]);

    this._patterns.set('czech', [
      /\ba\b|\bv\b|\bse\b|\bna\b|\bje\b|\bže\b|\bs\b|\bdo\b|\bjako\b|\bz\b|\bto\b|\bale\b|\bpro\b|\btak\b/gi, /[áčďéěíňóřšťúůýž]/gi]);

    this._patterns.set('danish', [
      /\bog\b|\bat\b|\bder\b|\bi\b|\bpå\b|\ber\b|\bsom\b|\bden\b|\btil\b|\bmed\b|\baf\b|\bet\b|\bfor\b|\bhar\b/gi, /[æøå]/gi]);

    this._patterns.set('dari', [
      /[\u0600-\u06FF]/g, /\bاز\b|\bدر\b|\bبه\b|\bاین\b|\bکه\b|\bرا\b|\bبا\b|\bاست\b/gi]);

    this._patterns.set('dutch', [
      /\bde\b|\bhet\b|\been\b|\bin\b|\bvan\b|\ben\b|\bte\b|\bdat\b|\bis\b|\bop\b|\bvoor\b|\bmet\b|\bzijn\b|\baan\b/gi, /[éëïóöüáç]/gi]);

    this._patterns.set('english', [
      /\bthe\b|\band\b|\bof\b|\bto\b|\ba\b|\bin\b|\bthat\b|\bis\b|\bfor\b|\byou\b|\bwith\b|\bhave\b|\bit\b|\bnot\b|\bon\b|\bbe\b|\bthis\b|\bwe\b|\bthey\b|\bat\b|\bbut\b|\bfrom\b|\bor\b|\bby\b|\bone\b|\bhad\b|\bwill\b|\bwhat\b|\ball\b|\bwere\b|\bwhen\b|\byour\b|\bcan\b|\bsaid\b|\bthere\b|\ban\b|\beach\b|\bwhich\b|\bshe\b|\bdo\b|\btheir\b|\bif\b|\bgood\b|\bher\b|\babout\b|\bupon\b|\binto\b|\bcould\b|\bother\b|\bhas\b|\bsome\b|\bmake\b|\blike\b|\bhim\b|\bnow\b|\bthan\b|\bthen\b|\blook\b|\bonly\b|\bcome\b|\bits\b|\bover\b|\bthink\b|\bback\b|\bafter\b|\buse\b|\bhow\b|\bour\b|\bfirst\b|\bwell\b|\bway\b|\beven\b|\bwant\b|\bbecause\b|\bany\b|\bthese\b|\bgive\b|\bmost\b|\bus\b|\bcat\b|\bdog\b|\bbird\b|\btree\b|\bstreet\b|\bcar\b|\bhouse\b|\bapple\b|\bwhite\b|\bblack\b|\bdouble\b|\btwo\b|\bthree\b|\bfour\b|\bpaper\b|\bletter\b|\bpuppy\b|\balso\b|\bbaby\b|\bchild\b|\bman\b|\bwoman\b|\bgirl\b|\bboy\b|\bfood\b|\bwater\b|\bair\b|\bearth\b|\bfire\b|\bbook\b|\bschool\b|\bwork\b|\bjob\b|\bmoney\b|\btime\b|\byear\b|\bmonth\b|\bweek\b|\bday\b|\bhour\b|\bminute\b|\bsecond\b|\bred\b|\bblue\b|\bgreen\b|\byellow\b|\bbrown\b|\bpurple\b|\bpink\b|\bgray\b|\bgrey\b|\borange\b|\bbig\b|\bsmall\b|\btall\b|\bshort\b|\blong\b|\bwide\b|\bnarrow\b|\bhigh\b|\blow\b|\bhot\b|\bcold\b|\bwarm\b|\bcool\b|\bnew\b|\bold\b|\byoung\b|\beasy\b|\bhard\b|\bfast\b|\bslow\b|\bearly\b|\blate\b|\bstrong\b|\bweak\b|\brich\b|\bpoor\b|\bclean\b|\bdirty\b|\bbuy\b|\bsell\b|\beat\b|\bdrink\b|\bsleep\b|\bwalk\b|\brun\b|\btalk\b|\bspeak\b|\bwrite\b|\bread\b|\bsee\b|\bhear\b|\bfeel\b|\btouch\b|\bsmell\b|\btaste\b|\bpeople\b|\bfamily\b|\bfriend\b|\bneighbor\b|\bteacher\b|\bstudent\b|\bdoctor\b|\bcountry\b|\bcity\b|\btown\b|\bvillage\b|\broom\b|\bbed\b|\bchair\b|\btable\b|\bdoor\b|\bwindow\b|\bwall\b|\bfloor\b|\bceiling\b|\broof\b|\bgarden\b|\bpark\b|\bfarm\b|\bfield\b|\bforest\b|\bmountain\b|\briver\b|\blake\b|\bocean\b|\bsea\b|\bsky\b|\bcloud\b|\brain\b|\bsnow\b|\bwind\b|\bsun\b|\bmoon\b|\bstar\b|\bcomputer\b|\bphone\b|\btv\b|\bradio\b|\bcamera\b|\bdrive\b|\bfly\b|\bswim\b/gi]);

    this._patterns.set('estonian', [
      /\bja\b|\bet\b|\bon\b|\bei\b|\bsee\b|\bka\b|\bning\b|\bkui\b|\boma\b|\boli\b|\bkõik\b/gi, /[äöõüš]/gi]);

    this._patterns.set('fijian', [
      /\bna\b|\bni\b|\be\b|\bka\b|\bkei\b|\byaco\b|\bmai\b|\boa\b|\bsa\b|\bkina\b|\btiko\b/gi]);

    this._patterns.set('filipino', [
      /\bang\b|\bng\b|\bsa\b|\bat\b|\bnang\b|\bay\b|\bna\b|\bako\b|\bpara\b|\bko\b|\bito\b/gi, /[ñ]/gi]);

    this._patterns.set('finnish', [
      /\bja\b|\bon\b|\bettä\b|\bei\b|\bmutta\b|\bovat\b|\boli\b|\bsen\b|\bhän\b|\bsitä\b|\bkun\b|\bniin\b|\btai\b/gi, /[äöå]/gi]);

    this._patterns.set('french', [
      /\ble\b|\bla\b|\bde\b|\bet\b|\bun\b|\bune\b|\ben\b|\best\b|\bque\b|\bpour\b|\bdans\b|\bce\b|\bà\b|\bdu\b|\bdes\b/gi, /[àâçéèêëîïôœùûü]/gi]);

    this._patterns.set('french_canada', [
      /\ble\b|\bla\b|\bde\b|\bet\b|\bun\b|\bune\b|\ben\b|\best\b|\bque\b|\bpour\b|\bdans\b|\bce\b|\bà\b|\bdu\b|\bdes\b/gi, /[àâçéèêëîïôœùûü]/gi]);

    this._patterns.set('galician', [/\bo\b|\ba\b|\bde\b|\be\b|\ben\b|\bque\b|\bun\b|\bunha\b|\bpara\b|\bcon\b|\bnon\b/gi, /[áéíóúüñ]/gi]);

    this._patterns.set('georgian', [
      /[\u10A0-\u10FF]/g, /\bდა\b|\bრომ\b|\bარ\b|\bარის\b|\bიყო\b|\bეს\b|\bთუ\b/gi]);

    this._patterns.set('german', [
      /\bder\b|\bdie\b|\bdas\b|\bund\b|\bin\b|\bist\b|\bden\b|\bvon\b|\bzu\b|\bmit\b|\bauf\b|\bein\b|\beine\b|\bsich\b/gi, /[äöüß]/gi]);

    this._patterns.set('greek', [
      /[\u0370-\u03FF]/g, /\bκαι\b|\bτο\b|\bτου\b|\bτην\b|\bτα\b|\bνα\b|\bαπό\b|\bμε\b|\bσε\b|\bπου\b|\bείναι\b/gi]);

    this._patterns.set('gujarati', [
      /[\u0A80-\u0AFF]/g, /છે|અને|કે|એક|પણ|માં|તે|ના/g]);

    // this._patterns.set('haitian', [
    //   /\bla\b|\byo\b|\bnous\b|\bpou\b|\ban\b|\bpa\b|\bki\b|\bak\b|\bli\b|\bsou\b|\bnan\b/gi]);

    this._patterns.set('haitian', [
      /\bla\b|\byo\b|\bnous\b|\bpou\b|\ban\b|\bpa\b|\bki\b|\bak\b|\bli\b|\bsou\b|\bnan\b|\bm\b|\bap\b|\bte\b|\bnap\b|\byap\b|\bpap\b|\bmwen\b|\btou\b|\bbay\b|\bgen\b|\bfè\b|\bse\b|\bou\b|\bale\b|\bvini\b|\bkou\b|\bsak\b|\bvi\b|\bwè\b/gi,
      /\bm\s(ap|te|a)\b|\bl\s(ap|te|a)\b|\bn\s(ap|te|a)\b|\by\s(ap|te|a)\b/gi
    ]);

    this._patterns.set('hausa', [
      /\bda\b|\bna\b|\bya\b|\bwa\b|\bbabu\b|\bwani\b|\bkuma\b|\bidan\b|\bko\b|\bce\b/gi]);

    this._patterns.set('hebrew', [
      /[\u0590-\u05FF\uFB1D-\uFB4F]/g, /\bאת\b|\bשל\b|\bלא\b|\bעל\b|\bאני\b|\bהוא\b|\bהיא\b|\bעם\b/gi]);

    this._patterns.set('hindi', [
      /[\u0900-\u097F]/g, /\bका\b|\bके\b|\bकी\b|\bहै\b|\bऔर\b|\bमें\b|\bसे\b|\bको\b|\bपर\b|\bएक\b/gi]);

    this._patterns.set('hmong', [
      /\bntawm\b|\bthiab\b|\bcov\b|\byog\b|\byam\b|\btias\b|\bntawd\b|\bnws\b|\bluag\b/gi]);

    this._patterns.set('hungarian', [
      /\ba\b|\baz\b|\bés\b|\begy\b|\bhogy\b|\bvan\b|\bnem\b|\bis\b|\bmeg\b|\bde\b|\bel\b|\bez\b|\bha\b/gi, /[áéíóöőúüű]/gi]);

    this._patterns.set('icelandic', [
      /\bog\b|\bað\b|\bí\b|\bsem\b|\ber\b|\bum\b|\baf\b|\btil\b|\bmeð\b|\ben\b|\bvið\b/gi, /[áðéíóúýþæö]/gi]);

    this._patterns.set('igbo', [
      /\bna\b|\bn'\b|\bma\b|\baka\b|\bnke\b|\bha\b|\bihe\b|\bolee\b|\behi\b|\bdị\b|\bm\b/gi]);

    this._patterns.set('indonesian', [
      /\bdan\b|\byang\b|\bdi\b|\bdalam\b|\bke\b|\bpada\b|\buntuk\b|\bdari\b|\bdengan\b|\btidak\b/gi]);

    this._patterns.set('irish', [
      /\bagus\b|\bar\b|\ban\b|\bgo\b|\bleis\b|\ba\b|\bsé\b|\bsí\b|\bsan\b|\broimh\b|\bchun\b/gi, /[áéíóú]/gi]);

    this._patterns.set('isixhosa', [
      /\bkunye\b|\binto\b|\babant\b|\bakukho\b|\benzima\b|\bezinye\b|\bkuba\b|\byaye\b|\bukuba\b/gi, /[áéíóúñ]/gi]);

    // this._patterns.set('italian', [
    //   /\bil\b|\bla\b|\be\b|\bdi\b|\bche\b|\bun\b|\bin\b|\ba\b|\bper\b|\bcon\b|\bmi\b|\bti\b|\bsi\b|\bnon\b|\bsono\b/gi, /[àèéìòù]/gi]);

    this._patterns.set('italian', [
      /\bil\b|\bla\b|\be\b|\bdi\b|\bche\b|\bun\b|\bin\b|\ba\b|\bper\b|\bcon\b|\bmi\b|\bti\b|\bsi\b|\bnon\b|\bquesta\b|\bquesti\b|\bquesto\b|\bsono\b|\bsei\b|\bè\b|\bsiamo\b|\bsiete\b|\bhaibiamo\b|\bfacciamo\b|\bdove\b|\bquando\b|\bperché\b|\bcome\b|\bcosa\b|\bchi\b/gi,
      /[àèéìòù]/gi,
      /\bgli\b|\blo\b|\bdal\b|\bdelle\b|\bdella\b|\bdello\b|\bdei\b|\bdai\b|\bdagli\b/gi,
      /\b\w+zione\b|\b\w+zioni\b|\b\w+mento\b|\b\w+mente\b|\b\w+issimo\b|\b\w+issima\b/gi
    ]);

    this._patterns.set('japanese', [
      /[\u3040-\u30FF\u3400-\u4DBF\u4E00-\u9FFF]/g, /です|ます|した|ません|ください/g]);

    this._patterns.set('kannada', [
      /[\u0C80-\u0CFF]/g, /ಮತ್ತು|ಆದರೆ|ಅವರು|ಇದು|ಅದು|ಇಲ್ಲ|ಆಗಿ/g]);

    this._patterns.set('kazakh', [
      /[\u0400-\u04FF]/g, /\bжәне\b|\bбар\b|\bбұл\b|\bда\b|\bмен\b|\bол\b/gi, /[әіңғүұқөһ]/gi]);

    this._patterns.set('khmer', [
      /[\u1780-\u17FF]/g, /នៅ|និង|ដែល|ក្នុង|មាន|ការ|ដើម្បី/g]);

    this._patterns.set('kinyarwanda', [
      /\bcy'\b|\bn'\b|\bmu\b|\bku\b|\bya\b|\bby'\b|\bw'\b|\bmuri\b|\bibyo\b|\bkuri\b/gi]);

    this._patterns.set('korean', [
      /[\uAC00-\uD7AF\u1100-\u11FF\u3130-\u318F]/g, /이|가|은|는|을|를|에|에서|의|하다/g]);

    this._patterns.set('kyrgyz', [
      /[\u0400-\u04FF]/g, /\bжана\b|\bда\b|\bбул\b|\bал\b|\bболуп\b|\bмен\b/gi, /[өүң]/gi]);

    this._patterns.set('lao', [
      /[\u0E80-\u0EFF]/g, /ແລະ|ໃນ|ທີ່|ການ|ຂອງ|ເປັນ|ໄດ້|ຈາກ|ກັບ|ຫຼື|ເຖິງ|ວ່າ|ໃຫ້|ກ່ຽວກັບ|ບໍ່/g]);

    this._patterns.set('latvian', [
      /\bun\b|\bir\b|\bar\b|\bka\b|\bno\b|\bpar\b|\bpie\b|\blir\b|\bbet\b|\btas\b|\bkā\b|\bšo\b/gi, /[āčēģīķļņšūž]/gi]);

    this._patterns.set('lithuanian', [
      /\bir\b|\byra\b|\bį\b|\bkaip\b|\bsu\b|\bnuo\b|\btai\b|\bkad\b|\bbet\b|\bper\b|\buž\b|\bant\b/gi, /[ąčęėįšųūž]/gi]);

    this._patterns.set('luxembourgish', [
      /\ban\b|\bden\b|\bdatt\b|\bzu\b|\bvun\b|\biech\b|\bdir\b|\bhien\b|\bmä\b|\bnet\b/gi, /[äéëî]/gi]);

    this._patterns.set('macedonian', [
      /[\u0400-\u04FF]/g, /\bи\b|\bво\b|\bна\b|\bсе\b|\bод\b|\bза\b|\bда\b|\bсо\b/gi, /[љњќѕџ]/gi]);

    this._patterns.set('malagasy', [
      /\bsy\b|\bny\b|\bna\b|\bdia\b|\btsy\b|\bao\b|\beo\b|\biza\b|\ba\b|\bmba\b|\bre\b|\bfa\b/gi]);

    this._patterns.set('malay', [
      /\bdan\b|\byang\b|\bdi\b|\bitu\b|\buntuk\b|\btidak\b|\bdalam\b|\bdari\b|\bdengan\b|\bakan\b/gi]);

    this._patterns.set('malayalam', [
      /[\u0D00-\u0D7F]/g, /ആണ്|എന്ന|കൂടി|അത്|ഒരു|ചെയ്ത/g]);

    // this._patterns.set('maltese', [
    //   /\bli\b|\bta\b|\bil\b|\bu\b|\bfi\b|\bbiex\b|\bma\b|\bhuma\b|\bkien\b|\bgħal\b/gi, /[ġħċż]/gi]);

    this._patterns.set('maltese', [
      /\bli\b|\bta\b|\bil\b|\bu\b|\bfi\b|\bbiex\b|\bma\b|\bkien\b|\bgħal\b|\bminn\b|\bjien\b|\binti\b|\bhuwa\b|\bhija\b|\baħna\b|\bintom\b|\bhuma\b|\bqed\b|\bta'\b|\btal-\b|\btiegħ\b|\btagħ\b|\b'l\b|\b'il\b/gi,
      /[ġħċżàèìòù]/gi,
      /għ\w+|\bil-\w+|\bit-\w+|\bid-\w+|\bim-\w+|\bir-\w+|\biż-\w+|\bis-\w+|ix-\w+/gi
    ]);

    this._patterns.set('maori', [
      /\bte\b|\bo\b|\bi\b|\bki\b|\bka\b|\be\b|\bnga\b|\bkua\b|\bko\b|\bhe\b|\bme\b|\btana\b/gi, /[āēīōū]/gi]);

    this._patterns.set('marathi', [
      /[\u0900-\u097F]/g, /\bकी\b|\bच्या\b|\bआहे\b|\bते\b|\bया\b|\bआणि\b|\bतो\b|\bती\b/gi]);

    this._patterns.set('mongolian', [
      /[\u1800-\u18AF\u0400-\u04FF]/g, /\bбол\b|\bнь\b|\bба\b|\bбайна\b|\bюм\b|\bгэж\b|\bхийж\b/gi, /[өүң]/gi]);

    this._patterns.set('nepali', [
      /[\u0900-\u097F]/g, /\bर\b|\bको\b|\bमा\b|\bछ\b|\bगर्न\b|\bहो\b|\bहुन\b|\bभएको\b/gi]);

    this._patterns.set('norwegian', [
      /\bog\b|\bi\b|\bdet\b|\bat\b|\ben\b|\ber\b|\bsom\b|\bpå\b|\bde\b|\bmed\b|\btil\b|\bhar\b|\bfor\b|\bikke\b/gi, /[æøå]/gi]);

    this._patterns.set('oriya', [
      /[\u0B00-\u0B7F]/g, /ଏହି|ଏବଂ|ହେବ|କରିବା|ସହିତ|ତାଙ୍କର|କରନ୍ତି/g]);

    this._patterns.set('portuguese', [/\bporque\b|\bquando\b|\btambém\b|\bmuito\b|\bentão\b|\bestá\b|\bsão\b|\bpelo\b|\bcomo\b|\bfoi\b|\besta\b|\bseus\b|\bsua\b|\bmas\b|\baté\b|\bquem\b|\bjá\b|\bnos\b|\bisso\b|\bsobre\b|\bpela\b|\bsem\b|\bmesmo\b|\bforam\b|\bhá\b|\bser\b|\bter\b|\bfaz\b|\bdepois\b|\bantes\b|\bportugal\b|\brapariga\b|\brapaz\b|\bcadeira\b|\bjanela\b|\binfelizmente\b|\bportanto\b|\bentretanto\b|\bnaquela\b|\bnaquele\b|\bembora\b|\btalvez\b|\bdiferente\b|\bconhecer\b|\bcertamente\b|\bencontrar\b|\bchegar\b|\bsonhar\b|\bpensar\b/gi, /[áàâãçéêíóôõú]/gi]);

    this._patterns.set('pashto', [
      /[\u0600-\u06FF\u0750-\u077F\u0880-\u08FF\uFB50-\uFDFF]/g, /\bد\b|\bاو\b|\bپه\b|\bچې\b|\bهغه\b|\bيې\b/gi]);

    this._patterns.set('persian', [
      /[\u0600-\u06FF\u0750-\u077F\u0880-\u08FF\uFB50-\uFDFF]/g, /\bاز\b|\bو\b|\bدر\b|\bبه\b|\bکه\b|\bاین\b|\bرا\b|\bبا\b|\bاست\b/gi]);

    this._patterns.set('polish', [
      /\bi\b|\bw\b|\bnie\b|\bna\b|\bdo\b|\bto\b|\bże\b|\bz\b|\bsię\b|\bo\b|\bjak\b|\bale\b|\bco\b|\btak\b/gi, /[ąćęłńóśźż]/gi]);

    this._patterns.set('punjabi', [
      /[\u0A00-\u0A7F]/g, /ਦੇ|ਵਿੱਚ|ਅਤੇ|ਦਾ|ਦੀ|ਇਸ|ਨੂੰ|ਹੈ|ਕਿ|ਉਹ/g]);

    this._patterns.set('romanian', [
      /\bși\b|\bde\b|\bîn\b|\ba\b|\bla\b|\bpe\b|\bcu\b|\bsă\b|\bnu\b|\bce\b|\bdin\b|\bpentru\b|\beste\b/gi, /[ăâîșț]/gi]);

    this._patterns.set('russian', [
      /[\u0400-\u04FF]/g, /\bи\b|\bв\b|\bна\b|\bс\b|\bне\b|\bчто\b|\bя\b|\bэто\b|\bон\b|\bона\b|\bк\b|\bу\b|\bвы\b/gi]);

    this._patterns.set('scottishGaelic', [
      /\btha\b|\bagus\b|\ban\b|\bna\b|\baig\b|\bair\b|\bann\b|\bchan\b|\beil\b|\bgu\b|\bmi\b|\bsin\b|\bseo\b|\ba'\b|\bcha\b|\bfad\b|\bfar\b|\bfo\b|\bgach\b|\bmar\b|\bnam\b|\bnan\b|\bnas\b/gi]);

    this._patterns.set('serbian', [
      /\bi\b|\bu\b|\bje\b|\bse\b|\bda\b|\bna\b|\bsu\b|\bne\b|\bs\b|\bkao\b|\bza\b|\bto\b|\bšto\b|\bali\b/gi, /[čćđšž]/gi]);

    this._patterns.set('sesotho', [
      /\bho\b|\ble\b|\bba\b|\bka\b|\bke\b|\bya\b|\bhore\b|\btsa\b|\bsa\b|\bhae\b|\bha\b/gi]);

    this._patterns.set('shona', [
      /\bne\b|\bva\b|\bkuti\b|\bkana\b|\bkutsi\b|\bzve\b|\bino\b|\basi\b|\bangu\b/gi]);

    this._patterns.set('sinhala', [
      /[\u0D80-\u0DFF]/g, /සහ|වන|විට|පිළිබඳ|ලෙස|මෙම|කරන/g]);

    this._patterns.set('slovak', [
      /\ba\b|\bv\b|\bsa\b|\bna\b|\bje\b|\bže\b|\bs\b|\bdo\b|\bako\b|\bz\b|\bto\b|\bale\b|\bpre\b|\btak\b/gi, /[áäčďéíĺľňóôŕšťúýž]/gi]);

    this._patterns.set('slovenian', [
      /\bin\b|\bv\b|\bse\b|\bna\b|\bje\b|\bda\b|\bs\b|\bza\b|\bki\b|\bz\b|\bso\b|\bo\b|\bpa\b|\bne\b/gi, /[čšž]/gi]);

    this._patterns.set('somali', [
      /\boo\b|\bin\b|\bka\b|\biyo\b|\bu\b|\bla\b|\bwaa\b|\bsi\b|\ban\b|\bkuma\b|\bugu\b/gi]);

    this._patterns.set('spanish', [
      /\bel\b|\bla\b|\bde\b|\by\b|\ben\b|\bque\b|\bun\b|\buna\b|\blos\b|\blas\b|\bpor\b|\bcon\b|\bno\b|\bse\b|\blo\b/gi, /[áéíóúñü¿¡]/gi]);

    this._patterns.set('swahili', [
      /\bna\b|\bya\b|\bwa\b|\bkwa\b|\bni\b|\bkuwa\b|\byake\b|\bkama\b|\bkatika\b|\bhiyo\b/gi]);

    this._patterns.set('swedish', [
      /\boch\b|\batt\b|\bdet\b|\bi\b|\bpå\b|\bär\b|\bsom\b|\ben\b|\bför\b|\bmed\b|\bav\b|\btill\b|\bhar\b|\binte\b/gi, /[åäö]/gi]);

    this._patterns.set('tagalog', [
      /\bang\b|\bng\b|\bsa\b|\bat\b|\bay\b|\bko\b|\bito\b|\bsiya\b|\bpara\b|\bnang\b/gi]);

    this._patterns.set('tajik', [
      /[\u0400-\u04FF]/g, /\bва\b|\bдар\b|\bки\b|\bаз\b|\bбо\b|\bин\b|\bон\b/gi, /[ғқҷҳ]/gi]);

    this._patterns.set('tamil', [
      /[\u0B80-\u0BFF]/g, /இந்த|மற்றும்|என்று|அவர்|போன்ற|அல்லது/g]);

    this._patterns.set('tatar', [
      /[\u0400-\u04FF]/g, /\bһәм\b|\bда\b|\bбу\b|\bул\b|\bбар\b|\bдип\b/gi, /[әөүҗңһ]/gi]);

    this._patterns.set('telugu', [
      /[\u0C00-\u0C7F]/g, /మరియు|ఉంది|లేదు|చేయడం|కోసం|వారు/g]);

    this._patterns.set('thai', [
      /[\u0E00-\u0E7F]/g, /ที่|ไม่|และ|ของ|ใน|มี|เป็น|ว่า|ให้|ได้/g]);

    this._patterns.set('tibetan', [
      /[\u0F00-\u0FFF]/g, /དང་|ནི|ལ་|གི་|དེ་|ཡིན་|མ་|ཞིག་|འདི་|ཡོད་|མིན་|ནས་|ཅིང་|འགྲོ་|བྱེད་|ཚང་མ་|རེད་/g]);

    this._patterns.set('tigrinya', [
      /[\u1200-\u137F]/g, /ኣብ|ናይ|ምስ|እዩ|ንዓና|እቲ|እዚ/g]);

    this._patterns.set('turkish', [
      /\bve\b|\bbu\b|\bde\b|\bir\b|\bbir\b|\biçin\b|\bne\b|\ben\b|\bile\b|\bda\b|\bo\b|\bama\b|\bçok\b/gi, /[çğıöşü]/gi]);

    this._patterns.set('turkmen', [
      /\bwe\b|\bbu\b|\bşol\b|\bbar\b|\bbilen\b|\bbir\b|\bmen\b|\bsen\b|\bol\b/gi, /[çäöüýňşž]/gi]);

    this._patterns.set('ukrainian', [
      /[\u0400-\u04FF]/g, /\bі\b|\bта\b|\bв\b|\bна\b|\bне\b|\bз\b|\bщо\b|\bу\b|\bдо\b|\bвід\b|\bяк\b|\bце\b/gi, /[ґєії]/gi]);

    this._patterns.set('urdu', [
      /[\u0600-\u06FF]/g, /\bاور\b|\bکے\b|\bمیں\b|\bکو\b|\bہے\b|\bکہ\b|\bسے\b|\bنے\b/gi]);

    this._patterns.set('vietnamese', [
      /[àáạảãâầấậẩẫăằắặẳẵèéẹẻẽêềếệểễìíịỉĩòóọỏõôồốộổỗơờớợởỡùúụủũưừứựửữỳýỵỷỹđ]/gi, /\bvà\b|\bcủa\b|\bcó\b|\bđã\b|\bkhông\b/gi]);

    this._patterns.set('welsh', [
      /\by\b|\byn\b|\bo\b|\bi\b|\ba\b|\bar\b|\byr\b|\bac\b|\bam\b|\bred\b|\bgyda\b/gi, /[âêîôûŵŷ]/gi]);

    this._patterns.set('yoruba', [
      /\bti\b|\bni\b|\bsi\b|\bfun\b|\blati\b|\bnaa\b|\bninu\b|\bko\b|\bjẹ\b|\bawọn\b|\bpẹlu\b|\bbẹẹ\b|\bbẹ\b|\bọjọ\b|\bogbo\b/gi, /[ẹọṣàáíú]/gi]);

    this._patterns.set('zulu', [
      /\bukuthi\b|\bkwi\b|\bnga\b|\bakukho\b|\bumuntu\b|\bkuye\b|\bkule\b|\bngo\b|\bezi\b|\bama\b|\bisi\b|\buku\b|\babantu\b|\bkwe\b|\biphepha\b|\blezi\b|\bukuba\b|\bphezu\b/gi, /[á]/gi]);

    // UNSUPPORTED LANGUAGE
    this._patterns.set('chechen', [
      /\bду\b|\bю\b|\bса\b|\bцу\b|\bкху\b|\bхир\b|\bдер\b|\bсан\b|\bхьо\b|\bхьа\b|\bма\b|\bхӏун\b|\bиза\b|\bваша\b|\bсоь\b|\bвай\b|\bдац\b|\bдоь\b|\bбу\b|\bхьан\b|\bлаь\b|\bдан\b|\bкхуз\b/gi]);

    this._patterns.set('chuvash', [
      /\bпулӑҫ\b|\bвӑл\b|\bчи\b|\bтата\b|\bҫук\b|\bпур\b|\bпит\b|\bку\b|\bкун\b|\bатте\b|\bманӑн\b|\bсанӑн\b|\bхӑш\b|\bмӗн\b|\bҫак\b|\bара\b|\bпысӑк\b|\bкӑна\b|\bхула\b|\bҫын\b|\bҫӗр\b|\bҫӗнӗ\b/gi]);

    this._patterns.set('guarani', [
      /\bha'e\b|\bha\b|\bhína\b|\bnde\b|\bche\b|\bupe\b|\bko\b|\bla\b|\bpe\b|\bha'ekuéra\b|\boje\b|\bporã\b|\breho\b|\bavei\b|\brome\b|\bvaerã\b|\bmba'e\b|\bmbohapy\b|\bmokõi\b|\bpeteĩ\b|\bpiko\b|\bporke\b/gi]);

    this._patterns.set('inuktitut', [
      /\bᐊᓇᓇ\b|\bᐊᓇᑕ\b|\bᐊᕙᖓ\b|\bᐃᓄᒃ\b|\bᐃᓄᐃᑦ\b|\bᐅᖃᕐᑐᖅ\b|\bᓄᓇ\b|\bᐃᒻᒪᖄ\b|\bᐊᒻᒪ\b|\bᐃᓄᒃᑎᑐᑦ\b|\bᐃᕐᓂᖅ\b|\bᐊᑕᐅᓯᖅ\b|\bᒪᕐᕉᒃ\b|\bᐱᖓᓱᑦ\b|\bᑎᓴᒪᑦ\b|\bᑕᓪᓕᒪᑦ\b|\bᐊᐃ\b|\bᓇᑯᕐᒦᒃ\b/gi]);

    this._patterns.set('kalaallisut', [
      /\buanga\b|\bilvit\b|\btaanna\b|\binuk\b|\bsuna\b|\bqanoq\b|\bnaamik\b|\baap\b|\btassa\b|\bkalaallit\b|\bnunaat\b|\bannerpaat\b|\bisiginnaarpaa\b|\bnerisassat\b|\bataatsit\b|\bmarluk\b|\bpingasut\b|\bsisamat\b|\btallimat\b|\billaa\b/gi]);

    this._patterns.set('mapudungun', [
      /\bfey\b|\bchi\b|\bkom\b|\bwenu\b|\bkiñe\b|\bepu\b|\bkülá\b|\bmeli\b|\bkechu\b|\bkayu\b|\bregle\b|\bpura\b|\baylla\b|\bmari\b|\bpeñi\b|\blanmen\b|\bchaw\b|\bñuke\b|\bantü\b|\bküyen\b|\bwanglen\b|\bko\b|\bruka\b/gi]);

    this._patterns.set('nahuatl', [
      /\bhuan\b|\bihuan\b|\bica\b|\bmelahuac\b|\bcualli\b|\bauh\b|\bamo\b|\bquema\b|\bnehuatl\b|\btehhuatl\b|\byehhuatl\b|\btlacatl\b|\bcihuatl\b|\bce\b|\bome\b|\byei\b|\bnahui\b|\bmacuilli\b|\bchicuace\b|\bchicome\b/gi]);

    this._patterns.set('navajo', [
      /\bhą́ą́\b|\bdooda\b|\bshí\b|\bní\b|\bhaash\b|\btʼáá\b|\báadi\b|\bayóo\b|\bdóó\b|\bdoo\b|\bshił\b|\bhaʼátʼíísh\b|\bhaʼátʼíí\b|\bháadę́ę́ʼ\b|\bła\b|\bnaki\b|\btáaʼ\b|\bdį́į́ʼ\b|\bashdlaʼ\b|\bhastą́ą́\b/gi]);

    this._patterns.set('occitan', [
      /\blo\b|\bla\b|\blos\b|\blas\b|\bde\b|\ba\b|\be\b|\ben\b|\bun\b|\buna\b|\bper\b|\bamb\b|\bque\b|\bpas\b|\bai\b|\bas\b|\bal\b|\bals\b|\bpèi\b|\bfòrça\b|\baquò\b|\bbèl\b|\bèsser\b|\baver\b/gi]);

    this._patterns.set('quechua', [
      /\bñuqa\b|\bqam\b|\bpay\b|\bnoqanchis\b|\bhuk\b|\biskay\b|\bkimsa\b|\btawa\b|\bpichqa\b|\bsachqa\b|\bqanchis\b|\bpusaq\b|\bisqun\b|\bchunka\b|\bhina\b|\bmana\b|\bari\b|\bama\b|\bmay\b|\bruna\b|\bllaqta\b/gi]);

    this._patterns.set('romansh', [
      /\bil\b|\bla\b|\bils\b|\blas\b|\be\b|\bed\b|\bin\b|\bina\b|\bda\b|\bcun\b|\bper\b|\bsco\b|\bche\b|\bma\b|\bagl\b|\bagls\b|\bsur\b|\bsut\b|\baver\b|\besser\b|\bèn\b|\bgliez\b|\bsurvart\b|\bsutsut\b/gi]);

    this._patterns.set('samoan', [
      /\bo\b|\ble\b|\be\b|\bma\b|\bona\b|\bsa\b|\bua\b|\blava\b|\bfai\b|\batu\b|\btou\b|\blau\b|\blona\b|\bsou\b|\bmatua\b|\bteine\b|\btama\b|\balofa\b|\bmanuia\b|\btasi\b|\blua\b|\btolu\b|\bfa\b|\blima\b/gi]);

    this._patterns.set('sardinian', [
      /\bsu\b|\bsa\b|\bsos\b|\bsas\b|\bde\b|\ba\b|\be\b|\bin\b|\bunu\b|\buna\b|\bpro\b|\bcun\b|\bchi\b|\bno\b|\bbi\b|\bsi\b|\best\b|\bat\b|\bitta\b|\dae\b|\bdeo\b|\bzente\b|\bdomo\b|\bmama\b/gi]);

    this._patterns.set('sindhi', [
      /\bجي\b|\bکي\b|\b۾\b|\bآهي\b|\bته\b|\bهو\b|\bتوهان\b|\bهن\b|\bمان\b|\bسان\b|\bپنهنجي\b|\bڇا\b|\bهوندو\b|\bهيءُ\b|\bوڃي\b|\bاچي\b|\bجيڪي\b|\bڪري\b|\bڪونهي\b|\bهڪ\b|\bٻه\b|\bٽي\b/gi]);

    this._patterns.set('tahitian', [
      /\be\b|\bte\b|\bo\b|\bia\b|\bi\b|\bta\b|\bno\b|\bmea\b|\bto\b|\bmai\b|\batu\b|\bfaʻa\b|\btau\b|\bhoa\b|\bora\b|\broa\b|\bhoʻi\b|\bhoʻe\b|\bpiti\b|\btoru\b|\bmaha\b|\brima\b|\bono\b|\bhitu\b/gi]);

    this._patterns.set('tswana', [
      /\bke\b|\ble\b|\bgo\b|\bya\b|\ba\b|\bba\b|\bo\b|\bwa\b|\bse\b|\bre\b|\bfela\b|\bmme\b|\brra\b|\bngwana\b|\bmotho\b|\bsengwe\b|\bntlo\b|\bnngwe\b|\bpedi\b|\bthataro\b|\bsupa\b|\brobedi\b|\brobongwe\b|\blesome\b/gi]);

    this._patterns.set('uighur', [
      /\bمەن\b|\bسەن\b|\bئۇ\b|\bبىز\b|\bسىلەر\b|\bئۇلار\b|\bبۇ\b|\bئاۋۇ\b|\bبار\b|\bيوق\b|\bھەئە\b|\bياق\b|\bقانداق\b|\bنېمە\b|\bنەدە\b|\bبىلەن\b|\bئۈچۈن\b|\bبىر\b|\bئىككى\b|\bئۈچ\b|\bتۆت\b/gi]);

    this._patterns.set('uzbek', [
      /\bmen\b|\bsen\b|\bu\b|\bbiz\b|\bsiz\b|\bular\b|\bbu\b|\bshu\b|\bva\b|\bham\b|\bbilan\b|\buchun\b|\bhaqqida\b|\bha\b|\byoq\b|\bqanday\b|\bnima\b|\bqayerda\b|\bbir\b|\bikki\b|\buch\b|\bturt\b|\bbesh\b|\bolti\b/gi]);

    this._patterns.set('wolof', [
      /\bna\b|\bla\b|\bi\b|\bak\b|\bam\b|\bdu\b|\bfi\b|\bli\b|\bnga\b|\bngi\b|\bsi\b|\bsu\b|\bagg\b|\baskan\b|\bbenn\b|\bñaar\b|\bñett\b|\bñeent\b|\bjuróom\b|\bjuróom-benn\b|\bjuróom-ñaar\b/gi]);
  }

  /**
   * Get patterns for a specific language
   * @param language Language name (e.g., "english")
   * @returns Array of RegExp patterns or undefined if not found
   */
  static getPatterns(language: string): RegExp[] | undefined {
    return this._patterns.get(language);
  }

  /**
   * Get all supported language names
   * @returns Array of language names
   */
  static getLanguages(): string[] {
    return Array.from(this._patterns.keys());
  }

  /**
   * Check if a language is supported
   * @param language Language name to check
   * @returns True if the language is supported
   */
  static hasLanguage(language: string): boolean {
    return this._patterns.has(language);
  }
}

// Initialize patterns when the module is loaded
LanguagePatterns.initialize();

/**
 * Legacy function to maintain backwards compatibility
 * Returns all patterns as an object
 */
export function getPatterns(): Record<string, RegExp[]> {
  const result: Record<string, RegExp[]> = {};

  for (const language of LanguagePatterns.getLanguages()) {
    const patterns = LanguagePatterns.getPatterns(language);
    if (patterns) {
      result[language] = patterns;
    }
  }

  return result;
}

// Export a constant to maintain backwards compatibility
export const patterns = getPatterns();
